import { PLANS } from "@riseafrica/scoring";
import { useEffect, useMemo, useState } from "react";
import { CONFIG } from "../config";
import { fmt } from "../lib/format";
import { Exploitation } from "./Exploitation";

/**
 * Le tableau de bord du produit.
 *
 * **Pourquoi cet écran existe.** Toutes ces mesures étaient déjà collectées, et
 * aucune n'était lisible : il fallait ouvrir une exécution GitHub Actions et
 * lire du JSON pour savoir où en était le produit. Ce qui revient à ne pas
 * savoir — on ne consulte pas un journal de déploiement pour prendre une
 * décision, on le consulte quand quelque chose a déjà cassé.
 *
 * **Ce qu'il montre que `/sante` ne montre pas.** Des volumes, une recette en
 * francs, un coût. `/sante` est ouvert et ne publie que des pourcentages, ce qui
 * est le bon arbitrage pour un point d'entrée public — mais laissait le porteur
 * du projet sans le moindre chiffre absolu sur son propre produit.
 *
 * **Ce que la première version ratait.** Elle affichait sept compteurs sur les
 * vingt-quatre que le produit tient déjà, et renvoyait le reste vers `/sante`.
 * Or ces compteurs vivent **dans les documents déjà lus** : les ignorer
 * n'économisait aucune lecture, seulement de la connaissance. Surtout, elle
 * donnait des totaux et aucun rapport entre eux — la recette, la dépense, la
 * marge, mais jamais *à partir de quel taux de conversion cela tient*. Trois
 * chiffres justes qui laissent la seule question ouverte.
 *
 * **Identifiant et mot de passe**, plutôt qu'un jeton dans l'adresse. Le jeton
 * était plus court à écrire et pénible à vivre : quarante-huit caractères à
 * recoller dans la barre d'adresse chaque fois, qui traînent dans l'historique
 * et partent avec le lien si on le partage.
 *
 * La session vit en `sessionStorage` et non `localStorage` : elle disparaît à la
 * fermeture de l'onglet. Sur un ordinateur partagé, c'est la différence entre
 * une porte qui se referme et une porte qu'on croit fermée.
 */

const CLE_SESSION = "stratera.pilotage.session";

type Serie = Record<string, number> & { jour: string };

interface Tendance {
  recent: number;
  precedent: number;
  variation: number | null;
}

interface Economie {
  coutParDiagnostic: number | null;
  coutParVente: number | null;
  recetteParDiagnostic: number | null;
  margeParDiagnostic: number | null;
  panierMoyen: number | null;
  conversionReelle: number | null;
  conversionSeuil: number | null;
}

interface Alerte {
  niveau: "rouge" | "orange";
  texte: string;
}

interface Pilotage {
  jours: number;
  /** La dernière marche de l'entonnoir est-elle mesurée sur cette fenêtre ? */
  remiseComptee?: boolean;
  ouvertureComptee?: boolean;
  joursTendance: number;
  serie: Serie[];
  cumul: Record<string, number> | null;
  taux: Record<string, number | null>;
  economie: Economie;
  tendances: Record<string, Tendance>;
  alertes: Alerte[];
  plafondQuotidienXof: number;
  /** L'entonnoir de chaque campagne, du plus vu au moins vu — absent avant le 06/09. */
  campagnes?: Campagne[];
}

interface Campagne {
  campagne: string;
  /** Absent des réponses antérieures au 07/10/2026. */
  arriveesCampagne?: number;
  vues: number;
  diagnostics: number;
  scores: number;
  paiementsOuverts: number;
  ventes: number;
  dirigeantsDemarres: number;
  dirigeantsResultats: number;
}

/*
  Les prix viennent du module partagé, pas de la réponse.

  Ils y vivent déjà — c'est le serveur qui recalcule le montant dû à partir du
  plan, et deux constantes qui divergeraient produiraient un paiement au mauvais
  prix. Les faire aussi voyager dans cette réponse ajoutait un champ à tenir, et
  son absence suffisait à vider l'écran : `fmt(undefined)` lève, React démonte
  la page, et il ne reste rien à lire.
*/

/*
  Un compteur absent vaut zéro, il ne vide pas l'écran.

  `fmt(undefined)` lève, React démonte la page, et il ne reste rien à lire —
  c'est ce qui est arrivé deux fois : une fois sur un prix, une fois sur un
  compteur ajouté côté serveur avant que le bouchon d'essai ne le connaisse. Or
  la sémantique est claire pour un compteur : absent du document du jour veut
  dire « jamais incrémenté », c'est-à-dire zéro. Le dire vaut mieux que
  disparaître.

  Cela ne s'applique pas aux **taux** ni aux **ratios**, où `null` garde son
  sens : « rien à diviser » n'est pas « zéro ».
*/
const n = (cumul: Record<string, number> | null, cle: string): number =>
  typeof cumul?.[cle] === "number" ? cumul[cle] : 0;

/** Un pourcentage, ou un tiret. Zéro et « rien à diviser » ne se confondent pas. */
const pc = (v: number | null | undefined): string =>
  v === null || v === undefined ? "—" : `${v} %`;

const xof = (v: number | null | undefined): string =>
  v === null || v === undefined ? "—" : `${fmt(v)} F`;

/**
 * Un chiffre clé et son mouvement sur sept jours.
 *
 * Le mouvement compte autant que le niveau : « quatre-vingts visites » ne dit
 * rien, « quatre-vingts, en hausse de moitié » dit tout. Une variation absente
 * s'écrit « — » et non « 0 % » — la semaine précédente était vide, il n'y a
 * rien à comparer.
 */
function Chiffre({
  libelle,
  valeur,
  tendance,
  precision,
}: {
  libelle: string;
  valeur: string;
  tendance?: Tendance;
  precision?: string;
}) {
  const v = tendance?.variation ?? null;
  const sens = v === null ? "" : v > 0 ? " pil-hausse" : v < 0 ? " pil-baisse" : "";
  return (
    <div className="pil-chiffre">
      <div className="pil-chiffre-lib">{libelle}</div>
      <div className="pil-chiffre-val">{valeur}</div>
      {tendance !== undefined && (
        <div className={`pil-chiffre-var${sens}`}>
          {v === null ? "—" : `${v > 0 ? "+" : ""}${v} %`}
          <span className="pil-chiffre-ref"> sur 7 jours</span>
        </div>
      )}
      {precision !== undefined && <div className="pil-chiffre-ref">{precision}</div>}
    </div>
  );
}

/**
 * Trente jours en barres, sans dépendance ajoutée.
 *
 * Une bibliothèque de graphiques pèserait plus que tout le reste de cet écran
 * pour dessiner des rectangles. Le SVG est ici plus court que son intégration
 * l'aurait été, et il hérite des jetons de couleur au lieu d'en apporter.
 *
 * `role="img"` et un résumé : un lecteur d'écran reçoit la tendance en une
 * phrase plutôt que trente rectangles muets, et le tableau des quatorze
 * derniers jours porte les valeurs exactes juste en dessous.
 */
function Courbe({ serie, champ, libelle }: { serie: Serie[]; champ: string; libelle: string }) {
  const valeurs = serie.map((j) => j[champ] ?? 0);
  const max = Math.max(1, ...valeurs);
  const largeur = 100 / Math.max(1, valeurs.length);

  return (
    <div className="pil-courbe">
      <svg
        viewBox="0 0 100 30"
        preserveAspectRatio="none"
        role="img"
        aria-label={`${libelle} sur ${serie.length} jours. Maximum ${fmt(max)}, total ${fmt(
          valeurs.reduce((a, b) => a + b, 0),
        )}.`}
      >
        {valeurs.map((v, i) => {
          const h = (v / max) * 28;
          return (
            <rect
              key={serie[i].jour}
              x={i * largeur + largeur * 0.15}
              y={30 - h}
              width={largeur * 0.7}
              height={Math.max(v > 0 ? 0.6 : 0, h)}
              className={i >= valeurs.length - 7 ? "pil-barre pil-barre-recent" : "pil-barre"}
            />
          );
        })}
      </svg>
      <div className="pil-courbe-key">
        <span>{serie[0]?.jour ?? ""}</span>
        <span>max {fmt(max)}</span>
        <span>{serie[serie.length - 1]?.jour ?? ""}</span>
      </div>
    </div>
  );
}

/** Une ligne de tableau à deux colonnes : poste et valeur. */
function Ligne({
  poste,
  valeur,
  fort,
  note,
}: {
  poste: string;
  valeur: string;
  fort?: boolean;
  note?: string;
}) {
  return (
    <tr>
      <td data-col="Poste">
        {fort === true ? <b>{poste}</b> : poste}
        {note !== undefined && <span className="pil-note">{note}</span>}
      </td>
      <td data-col="Valeur">{fort === true ? <b>{valeur}</b> : valeur}</td>
    </tr>
  );
}

/** Une étape de l'entonnoir, avec sa part de l'étape précédente et des visites. */
function Etape({
  nom,
  valeur,
  sur,
  surVues,
  inconnu = false,
}: {
  nom: string;
  valeur: number;
  sur: number | null;
  surVues: number;
  /**
   * La marche n'est pas mesurée sur cette fenêtre — le compteur est né après.
   *
   * Toute la ligne passe à « — », le nombre compris. Afficher zéro serait
   * affirmer qu'il ne s'est rien passé, alors qu'on ne sait pas : sur la marche
   * de la remise, cela reviendrait à annoncer que rien n'est jamais parti.
   */
  inconnu?: boolean;
}) {
  const part = (n: number, d: number): string =>
    d > 0 ? `${Math.round((n / d) * 1000) / 10} %` : "—";
  return (
    <tr>
      <td data-col="Étape">{nom}</td>
      <td data-col="Nombre">{inconnu ? "—" : fmt(valeur)}</td>
      <td data-col="Part">{inconnu || sur === null ? "—" : part(valeur, sur)}</td>
      <td data-col="Des visites">{inconnu || sur === null ? "—" : part(valeur, surVues)}</td>
    </tr>
  );
}

const COURBES = [
  { champ: "vues", libelle: "Visites" },
  { champ: "diagnostics", libelle: "Diagnostics" },
  { champ: "partages", libelle: "Partages" },
  { champ: "ventes", libelle: "Ventes" },
  { champ: "recetteXof", libelle: "Recette" },
  { champ: "depenseXof", libelle: "Coût IA" },
] as const;

export function Pilotage() {
  const [donnees, setDonnees] = useState<Pilotage | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [courbe, setCourbe] = useState<string>("vues");
  const [session, setSession] = useState<string>(
    () => sessionStorage.getItem(CLE_SESSION) ?? "",
  );
  const [identifiant, setIdentifiant] = useState("");
  const [motDePasse, setMotDePasse] = useState("");
  const [nouveau, setNouveau] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [doitChanger, setDoitChanger] = useState(false);
  const [enCours, setEnCours] = useState(false);

  const ouvrir = (jeton: string) => {
    sessionStorage.setItem(CLE_SESSION, jeton);
    setSession(jeton);
    setErreur(null);
    setMotDePasse("");
    setNouveau("");
    setConfirmation("");
    setDoitChanger(false);
  };

  const poster = async (chemin: string, corps: Record<string, string>) => {
    setEnCours(true);
    setErreur(null);
    try {
      const r = await fetch(`${CONFIG.pilotageEndpoint}${chemin}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(corps),
      });
      const reponse = (await r.json()) as {
        session?: string;
        doitChanger?: boolean;
        raison?: string;
      };
      if (r.status === 429) {
        setErreur("Trop de tentatives. Réessayez dans une heure.");
        return;
      }
      if (r.status === 503) {
        setErreur("Le tableau de bord n'est pas configuré côté serveur.");
        return;
      }
      if (r.status === 400) {
        setErreur(reponse.raison ?? "Mot de passe refusé.");
        return;
      }
      if (!r.ok) {
        // Un seul message, quelle que soit la valeur fausse : le contraire
        // apprendrait à un inconnu qu'il a trouvé la bonne adresse.
        setErreur("Identifiant ou mot de passe incorrect.");
        return;
      }
      if (reponse.doitChanger) {
        setDoitChanger(true);
        return;
      }
      if (reponse.session) ouvrir(reponse.session);
    } catch {
      setErreur("Le serveur n'a pas répondu.");
    } finally {
      setEnCours(false);
    }
  };

  useEffect(() => {
    if (!session) return;
    let vivant = true;
    /*
      La session part dans un en-tête, pas dans l'adresse — 02/09/2026.

      `?session=…` l'écrivait dans les journaux d'accès de l'hébergeur, dans
      l'historique du navigateur et dans l'en-tête `Referer` de toute navigation
      sortante. Le serveur acceptait déjà `x-pilotage`, prévu pour cela ;
      personne ne l'envoyait. Ce qui voyageait là ouvre l'administration.
    */
    fetch(CONFIG.pilotageEndpoint, { headers: { "x-pilotage": session } })
      .then(async (r) => {
        if (r.status === 401) {
          // Session expirée ou secret changé : on repart de l'écran d'entrée
          // plutôt que d'afficher un message que l'on ne peut pas corriger.
          sessionStorage.removeItem(CLE_SESSION);
          setSession("");
          throw new Error("Session expirée. Reconnectez-vous.");
        }
        if (!r.ok) throw new Error(`Le serveur répond ${r.status}.`);
        return (await r.json()) as Pilotage;
      })
      .then((corps) => {
        if (vivant) setDonnees(corps);
      })
      .catch((e: Error) => {
        if (vivant) setErreur(e.message);
      });
    return () => {
      vivant = false;
    };
  }, [session]);

  const c = donnees?.cumul ?? null;
  const t = donnees?.taux ?? {};
  const eco = donnees?.economie;
  const recents = useMemo(() => (donnees?.serie ?? []).slice(-14).reverse(), [donnees]);

  /*
    L'économie tient-elle ? Une seule comparaison, faite une fois.

    Le taux réel au-dessus du seuil, chaque visiteur supplémentaire enrichit ;
    en dessous, il coûte. C'est la phrase que cet écran existe pour dire.
  */
  /*
    L'onglet courant vit dans le fragment d'adresse, pas dans un état seul.

    Ce tableau se recharge : on y revient après une action, on le rafraîchit
    quand on doute d'un chiffre, on le rouvre depuis un signet. Un onglet gardé
    en mémoire seule ramènerait chaque fois sur « à traiter », y compris pour
    quelqu'un qui passait sa matinée dans les mesures. Le fragment survit au
    rechargement et se partage — deux propriétés qu'un `useState` n'a pas.
  */
  const [vue, setVue] = useState<Vue>(() => vueDepuisFragment());

  useEffect(() => {
    const relire = () => setVue(vueDepuisFragment());
    window.addEventListener("hashchange", relire);
    return () => window.removeEventListener("hashchange", relire);
  }, []);

  const choisir = (v: Vue) => {
    setVue(v);
    window.location.hash = v === "agir" ? "" : v;
    /*
      On remonte en haut. Sans cela, changer d'onglet depuis le bas d'une page
      de trois mille pixels laisse devant un vide : les sections de la nouvelle
      vue sont plus haut, et rien ne le dit.
    */
    window.scrollTo({ top: 0, behavior: "auto" });
  };

  const tient =
    eco?.conversionReelle !== null &&
    eco?.conversionReelle !== undefined &&
    eco.conversionSeuil !== null &&
    eco.conversionReelle >= eco.conversionSeuil;

  return (
    <section id="contenu" data-vue={vue}>
      <div className="shell app">
        <div className="kicker">Pilotage — interne</div>
        <h1>Où en est le produit</h1>

        {erreur !== null && <div className="notice">{erreur}</div>}

        {session !== "" && !doitChanger && <Barre vue={vue} choisir={choisir} />}

        {!session && !doitChanger && (
          <form
            className="block"
            onSubmit={(e) => {
              e.preventDefault();
              void poster("/connexion", { identifiant, motDePasse });
            }}
          >
            <div className="field">
              <label htmlFor="pil-id">Identifiant</label>
              <input
                id="pil-id"
                type="email"
                autoComplete="username"
                value={identifiant}
                onChange={(e) => setIdentifiant(e.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="pil-mdp">Mot de passe</label>
              <input
                id="pil-mdp"
                type="password"
                autoComplete="current-password"
                value={motDePasse}
                onChange={(e) => setMotDePasse(e.target.value)}
              />
            </div>
            <button className="btn" type="submit" disabled={enCours}>
              {enCours ? "Vérification…" : "Entrer"}
            </button>
          </form>
        )}

        {doitChanger && (
          <form
            className="block"
            onSubmit={(e) => {
              e.preventDefault();
              if (nouveau !== confirmation) {
                setErreur("Les deux saisies diffèrent.");
                return;
              }
              void poster("/motdepasse", { identifiant, ancien: motDePasse, nouveau });
            }}
          >
            {/*
              Le changement est imposé, pas proposé. Le mot de passe d'amorçage
              a servi à ouvrir cette page une fois et n'ouvre rien d'autre : la
              réponse de connexion ne contient aucune session tant qu'il n'a pas
              été remplacé.
            */}
            <div className="notice">
              <b>Choisissez votre mot de passe.</b> Celui d'amorçage ne donne accès à rien : il
              sert uniquement à arriver ici. Douze caractères au minimum.
            </div>
            <div className="field">
              <label htmlFor="pil-new">Nouveau mot de passe</label>
              <input
                id="pil-new"
                type="password"
                autoComplete="new-password"
                value={nouveau}
                onChange={(e) => setNouveau(e.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="pil-new2">Confirmez</label>
              <input
                id="pil-new2"
                type="password"
                autoComplete="new-password"
                value={confirmation}
                onChange={(e) => setConfirmation(e.target.value)}
              />
            </div>
            <button className="btn" type="submit" disabled={enCours}>
              {enCours ? "Enregistrement…" : "Enregistrer et entrer"}
            </button>
          </form>
        )}

        {/*
          L'exploitation avant la mesure.

          Ce qui concerne quelqu'un qui a donné de l'argent passe avant les
          tendances : un acheteur qui attend son rapport ne se voit dans aucune
          courbe. Cette section s'affiche dès que la session est ouverte, même
          si les mesures ne sont pas encore chargées — le jour où le tableau de
          bord peine, c'est précisément là qu'il faut pouvoir agir.
        */}
        {session !== "" && !doitChanger && <Exploitation session={session} />}

        {c !== null && donnees !== null && eco !== undefined && (
          <div aria-live="polite">
            {/*
              Ce qui mérite qu'on agisse, avant les tableaux.

              Un rapport rend trente chiffres justes et laisse trouver lequel
              compte ; à trente, personne ne le trouve. Les seuils vivent côté
              serveur, en clair, parce que ce sont des jugements et qu'ils
              doivent se relire.
            */}
            {donnees.alertes.length > 0 && (
              <div data-groupe="agir" className="block pil-alertes">
                <h2>À regarder aujourd'hui</h2>
                {donnees.alertes.map((a) => (
                  <p key={a.texte} className={`pil-alerte pil-${a.niveau}`}>
                    {a.texte}
                  </p>
                ))}
              </div>
            )}

            <div data-groupe="mesure" className="block">
              <h2>Les chiffres, sur {donnees.jours} jours</h2>
              <div className="pil-chiffres">
                <Chiffre
                  libelle="Visites"
                  valeur={fmt(n(c, "vues"))}
                  tendance={donnees.tendances.vues}
                />
                <Chiffre
                  libelle="Diagnostics"
                  valeur={fmt(n(c, "diagnostics"))}
                  tendance={donnees.tendances.diagnostics}
                />
                <Chiffre
                  libelle="Indices affichés"
                  valeur={fmt(n(c, "scores"))}
                  tendance={donnees.tendances.scores}
                />
                <Chiffre
                  libelle="Partages"
                  valeur={fmt(n(c, "partages"))}
                  tendance={donnees.tendances.partages}
                  precision="le seul canal d'acquisition"
                />
                <Chiffre
                  libelle="Ventes"
                  valeur={fmt(n(c, "ventes"))}
                  tendance={donnees.tendances.ventes}
                />
                <Chiffre
                  libelle="Recette"
                  valeur={xof(n(c, "recetteXof"))}
                  tendance={donnees.tendances.recetteXof}
                />
                <Chiffre
                  libelle="Coût du modèle"
                  valeur={xof(n(c, "depenseXof"))}
                  precision={`plafond ${fmt(donnees.plafondQuotidienXof)} F par jour`}
                />
                <Chiffre
                  libelle="Marge"
                  valeur={xof(n(c, "margeXof"))}
                  precision="recette moins le modèle"
                />
              </div>
            </div>

            <div data-groupe="mesure" className="block">
              <h2>Jour par jour</h2>
              <div className="pil-onglets" role="group" aria-label="Choix de la mesure affichée">
                {COURBES.map((k) => (
                  <button
                    key={k.champ}
                    type="button"
                    className={`pil-onglet${courbe === k.champ ? " pil-onglet-actif" : ""}`}
                    aria-pressed={courbe === k.champ}
                    onClick={() => setCourbe(k.champ)}
                  >
                    {k.libelle}
                  </button>
                ))}
              </div>
              <Courbe
                serie={donnees.serie}
                champ={courbe}
                libelle={COURBES.find((k) => k.champ === courbe)?.libelle ?? courbe}
              />
              <p className="fine">
                Les sept dernières barres sont teintées : c'est la fenêtre que compare la
                tendance. Un jour à zéro n'est pas une panne, c'est un jour sans visiteur.
              </p>
            </div>

            <div data-groupe="mesure" className="block">
              <h2>L'entonnoir</h2>
              <p className="sub">
                Chaque part se lit d'abord sur l'étape précédente, puis sur les visites — les deux
                ne disent pas la même chose : la première désigne la marche qui casse, la seconde
                ce qu'il reste au bout.
              </p>
              {donnees.remiseComptee !== true && (
                <p className="fine">
                  {/*
                    Une mesure qui naît a un passé qu'elle n'a pas vu. Le dire
                    coûte une ligne ; le taire ferait lire « aucun rapport n'est
                    parti » sur des ventes toutes honorées.
                  */}
                  La dernière marche est comptée depuis le 26 août seulement. Les ventes
                  antérieures à cette fenêtre ont bien été remises&nbsp;— elles ne sont pas
                  comptées, et la ligne affiche donc «&nbsp;—&nbsp;» plutôt que zéro.
                </p>
              )}
              {donnees.ouvertureComptee !== true && (
                <p className="fine">
                  Les ouvertures sont comptées depuis le 1<sup>er</sup> septembre seulement. Les
                  rapports remis avant ont pu être lus sans que rien ne l'enregistre&nbsp;: la
                  ligne affiche «&nbsp;—&nbsp;» plutôt que zéro.
                </p>
              )}
              <div className="tabwrap">
                <table className="tab compare">
                  <thead>
                    <tr>
                      <th>Étape</th>
                      <th>Nombre</th>
                      <th>De l'étape précédente</th>
                      <th>Des visites</th>
                    </tr>
                  </thead>
                  <tbody>
                    <Etape nom="Visites" valeur={n(c, "vues")} sur={null} surVues={n(c, "vues")} />
                    <Etape
                      nom="Diagnostics lancés"
                      valeur={n(c, "diagnostics")}
                      sur={n(c, "vues")}
                      surVues={n(c, "vues")}
                    />
                    <Etape
                      nom="Indices affichés"
                      valeur={n(c, "scores")}
                      sur={n(c, "diagnostics")}
                      surVues={n(c, "vues")}
                    />
                    <Etape nom="Partages" valeur={n(c, "partages")} sur={n(c, "scores")} surVues={n(c, "vues")} />
                    <Etape
                      nom="Paiements ouverts"
                      valeur={n(c, "paiementsOuverts")}
                      sur={n(c, "scores")}
                      surVues={n(c, "vues")}
                    />
                    <Etape
                      nom="Ventes confirmées"
                      valeur={n(c, "ventes")}
                      sur={n(c, "paiementsOuverts")}
                      surVues={n(c, "vues")}
                    />
                    {/*
                      La dernière marche, ajoutée le 26/08.

                      L'entonnoir s'arrêtait sur l'encaissement, c'est-à-dire
                      sur le moment où l'argent entre. Celle-ci dit s'il a été
                      **gagné** : le document est-il parti ? C'est la seule
                      marche du tableau où le produit peut prendre et ne rien
                      donner, et elle manquait.

                      Elle peut dépasser cent pour cent sans anomalie : un
                      rapport vendu hier et relancé aujourd'hui se compte dans
                      les remises du jour et dans les ventes de la veille. Un
                      rattrapage doit se voir.
                    */}
                    <Etape
                      nom="Rapports remis"
                      valeur={n(c, "rapportsLivres")}
                      sur={n(c, "ventes")}
                      surVues={n(c, "vues")}
                      inconnu={donnees.remiseComptee !== true}
                    />
                    {/*
                      La marche d'après, ajoutée le 01/09.

                      « Remis » dit qu'un message est parti, pas qu'il est
                      arrivé, et encore moins qu'il a été ouvert. Une adresse
                      mal saisie, un message classé indésirable, un lien jamais
                      rouvert&nbsp;: trois pannes que personne ne signale, parce
                      que l'acheteur ne réclame pas et que rien ne les montrait.

                      Le numérateur compte les **premières** ouvertures&nbsp;:
                      un acheteur qui rouvre son rapport sur trois appareils est
                      un acheteur, pas trois. Le volume brut, relectures
                      comprises, se lit dans « Ouvertures ».
                    */}
                    <Etape
                      nom="Rapports ouverts"
                      valeur={n(c, "rapportsPremieresOuvertures")}
                      sur={n(c, "rapportsLivres")}
                      surVues={n(c, "vues")}
                      inconnu={donnees.ouvertureComptee !== true}
                    />
                  </tbody>
                </table>
              </div>
            </div>

            <div data-groupe="mesure" className="block">
              <h2>L'économie tient-elle ?</h2>
              {/*
                La seule question qui engage, posée en deux nombres.

                Recette, dépense et marge disent où l'on en est ; aucun ne dit
                si cela peut tenir. Le seuil le dit — et il ne demande ni plus
                de trafic ni un prix plus élevé pour être lu.
              */}
              <div className={`pil-verdict${tient ? " pil-verdict-ok" : ""}`}>
                <div className="pil-verdict-paire">
                  <div>
                    <div className="pil-chiffre-lib">Conversion réelle</div>
                    <div className="pil-verdict-num">{pc(eco.conversionReelle)}</div>
                  </div>
                  <div className="pil-verdict-sep" aria-hidden="true">
                    contre
                  </div>
                  <div>
                    <div className="pil-chiffre-lib">Seuil d'équilibre</div>
                    <div className="pil-verdict-num">{pc(eco.conversionSeuil)}</div>
                  </div>
                </div>
                <p>
                  {eco.conversionSeuil === null
                    ? "Pas encore assez d'indices affichés pour calculer un seuil."
                    : tient
                      ? "Au-dessus du seuil : chaque visiteur supplémentaire enrichit le produit."
                      : "Sous le seuil : chaque visiteur supplémentaire coûte plus qu'il ne rapporte. Le levier est le coût du modèle ou la conversion, pas le trafic."}
                </p>
              </div>
              <div className="tabwrap">
                <table className="tab compare">
                  <tbody>
                    <Ligne poste="Recette encaissée" valeur={xof(n(c, "recetteXof"))} />
                    <Ligne poste="Coût du modèle" valeur={xof(n(c, "depenseXof"))} />
                    <Ligne poste="Marge" valeur={xof(n(c, "margeXof"))} fort />
                    <Ligne
                      poste="Coût par diagnostic"
                      valeur={xof(eco.coutParDiagnostic)}
                      note="ce que coûte un visiteur servi gratuitement"
                    />
                    <Ligne
                      poste="Recette par diagnostic"
                      valeur={xof(eco.recetteParDiagnostic)}
                      note="ce que le même visiteur rapporte en moyenne"
                    />
                    <Ligne
                      poste="Marge par diagnostic"
                      valeur={xof(eco.margeParDiagnostic)}
                      fort
                    />
                    <Ligne
                      poste="Coût du modèle par vente"
                      valeur={xof(eco.coutParVente)}
                    />
                    <Ligne
                      poste="Panier moyen"
                      valeur={xof(eco.panierMoyen)}
                      note={`rapport ${fmt(PLANS.report)} F, formule ${fmt(PLANS.bundle)} F`}
                    />
                    <Ligne poste="Appels au modèle" valeur={fmt(n(c, "appelsModele"))} />
                    <Ligne
                      poste="Jetons consommés"
                      valeur={`${fmt(n(c, "tokensIn"))} entrée / ${fmt(n(c, "tokensOut"))} sortie`}
                    />
                  </tbody>
                </table>
              </div>
            </div>

            <div data-groupe="mesure" className="block">
              <h2>L'acquisition</h2>
              <p className="sub">
                Le canal payant ne ferme pas à {fmt(PLANS.report)} F : la croissance repose
                entièrement sur le partage. C'est l'indicateur à suivre avant tous les autres.
              </p>
              <div className="tabwrap">
                <table className="tab compare">
                  <tbody>
                    <Ligne poste="Partages" valeur={fmt(n(c, "partages"))} />
                    <Ligne
                      poste="Part des résultats partagés"
                      valeur={pc(t.partage)}
                      fort
                      note="le taux de parrainage"
                    />
                    <Ligne poste="Arrivées par un lien parrainé" valeur={fmt(n(c, "arriveesParrainees"))} />
                    <Ligne
                      poste="Arrivées par une campagne (utm)"
                      valeur={fmt(n(c, "arriveesCampagne"))}
                      note="l'entonnoir de chaque campagne est au tableau suivant"
                    />
                    <Ligne
                      poste="Bandeau Meta accepté"
                      valeur={
                        n(c, "metaBandeaux") > 0
                          ? `${Math.round((n(c, "metaAccords") / n(c, "metaBandeaux")) * 100)} %`
                          : "—"
                      }
                      note={`${fmt(n(c, "metaAccords"))} accords, ${fmt(n(c, "metaRefus"))} refus sur ${fmt(n(c, "metaBandeaux"))} affichages — Meta ne voit que les accords`}
                    />
                    <Ligne
                      poste="Achats signalés à Meta"
                      valeur={fmt(n(c, "metaAchats"))}
                      note={
                        n(c, "metaEchecs") > 0
                          ? `${fmt(n(c, "metaEchecs"))} refusés par Meta — voir les journaux « meta_achat_refuse »`
                          : "seulement les acheteurs qui ont accepté le bandeau"
                      }
                    />
                    <Ligne
                      poste="Visiteurs ramenés par partage"
                      valeur={
                        n(c, "partages") > 0
                          ? String(Math.round((n(c, "arriveesParrainees") / n(c, "partages")) * 100) / 100)
                          : "—"
                      }
                      note="au-dessus de 1, la boucle s'entretient"
                    />
                  </tbody>
                </table>
              </div>
            </div>

            <div data-groupe="mesure" className="block" data-bloc="campagnes">
              <h2>Par campagne</h2>
              <p className="sub">
                Ce que chaque annonce a produit, de la première vue à la vente. Une campagne se
                nomme dans le lien : <code>?utm_source=meta&amp;utm_campaign=dirigeant-01</code>,
                et le nom de campagne suit la session jusqu'au paiement. Une campagne tient si
                elle ferme au moins une vente pour ce qu'elle a coûté ; c'est ici qu'on le lit,
                annonce par annonce.
              </p>
              {donnees.campagnes === undefined || donnees.campagnes.length === 0 ? (
                <p className="pil-vide">
                  Aucune arrivée de campagne sur {donnees.jours} jours. Dès qu'un lien marqué
                  amène quelqu'un, sa ligne apparaît ici.
                </p>
              ) : (
                <div className="tabwrap">
                  <table className="tab campagnes">
                    <thead>
                      <tr>
                        <th scope="col">Campagne</th>
                        <th scope="col">Arrivées</th>
                        <th scope="col">Vues</th>
                        <th scope="col">Diagnostics</th>
                        <th scope="col">Indices vus</th>
                        <th scope="col">Dirigeants</th>
                        <th scope="col">Paiements ouverts</th>
                        <th scope="col">Ventes</th>
                        <th scope="col">Vues par vente</th>
                      </tr>
                    </thead>
                    <tbody>
                      {donnees.campagnes.map((k) => (
                        <tr key={k.campagne}>
                          <th scope="row">{k.campagne}</th>
                          <td>{fmt(k.arriveesCampagne ?? 0)}</td>
                          <td>{fmt(k.vues)}</td>
                          <td>{fmt(k.diagnostics)}</td>
                          <td>{fmt(k.scores)}</td>
                          <td>
                            {fmt(k.dirigeantsResultats)}
                            {k.dirigeantsDemarres > 0 && (
                              <span className="pil-note">sur {fmt(k.dirigeantsDemarres)} entrés</span>
                            )}
                          </td>
                          <td>{fmt(k.paiementsOuverts)}</td>
                          <td>
                            <b>{fmt(k.ventes)}</b>
                          </td>
                          <td>{k.ventes > 0 ? fmt(Math.round(k.vues / k.ventes)) : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div data-groupe="sante" className="block">
              <h2>Le service tient-il ?</h2>
              <div className="tabwrap">
                <table className="tab compare">
                  <tbody>
                    <Ligne
                      poste="Servi par le cache"
                      valeur={pc(t.cache)}
                      fort
                      note="chaque défaut est une génération payée"
                    />
                    <Ligne
                      poste="dont formulations apprises"
                      valeur={pc(t.cacheAppris)}
                      note="doit monter avec le temps, sinon la base n'apprend rien"
                    />
                    <Ligne
                      poste="Mode dégradé"
                      valeur={pc(t.degrade)}
                      note="le service répond sans faire son travail"
                    />
                    <Ligne poste="Abandon avant l'indice" valeur={pc(t.abandonAvantScore)} />
                    <Ligne
                      poste="dont partis pendant l'attente"
                      valeur={pc(t.abandonAttente)}
                      note="mode métier — accuse le temps de génération"
                    />
                    <Ligne
                      poste="dont renvoyés par une erreur"
                      valeur={pc(t.erreurMetier)}
                      note="métier non reconnu ou panne"
                    />
                    <Ligne
                      poste="dont partis devant les tâches"
                      valeur={pc(t.abandonTaches)}
                      note="accuse la longueur de l'écran des tâches"
                    />
                    <Ligne
                      poste="Paiement : partis avant le portefeuille"
                      valeur={pc(t.abandonPortefeuille)}
                    />
                    <Ligne
                      poste="Paiement : partis au formulaire"
                      valeur={pc(t.abandonFormulaire)}
                      note="numéro, nom, adresse"
                    />
                    <Ligne
                      poste="Au départ de la caisse : réponses"
                      valeur={fmt(n(c, "retenus"))}
                      note={`prix ${fmt(n(c, "retenusPrix"))} · voir plus ${fmt(n(c, "retenusApercu"))} · pas maintenant ${fmt(n(c, "retenusPlusTard"))} · moyen de paiement ${fmt(n(c, "retenusPaiement"))} · confiance ${fmt(n(c, "retenusConfiance"))}`}
                    />
                    <Ligne
                      poste="Paiement : refusés avant l'opérateur"
                      valeur={pc(t.refusAvantOperateur)}
                      note={`« Payer » pressé, aucune caisse ouverte chez Chariow · ${fmt(n(c, "paiementsEchoues"))} refus signalés`}
                    />
                    <Ligne
                      poste="Paiement : partis chez l'opérateur"
                      valeur={pc(t.abandonCaisse)}
                      note="caisse Chariow ouverte, aucune vente : page Chariow ou validation sur le téléphone"
                    />
                    <Ligne poste="Action non rédigée" valeur={pc(t.planEchec)} />
                    <Ligne
                      poste="Rapports payés non délivrés"
                      valeur={fmt(n(c, "rapportsEchec"))}
                      fort
                      note="le seul chiffre qui concerne quelqu'un ayant payé"
                    />
                    <Ligne poste="Paiements échoués" valeur={fmt(n(c, "paiementsEchoues"))} />
                    <Ligne
                      poste="Remboursements demandés"
                      valeur={fmt(n(c, "remboursementsDemandes"))}
                      fort
                      note="promis sans justification, sous quatorze jours"
                    />
                    <Ligne
                      poste="Écritures de cache échouées"
                      valeur={fmt(n(c, "cacheEcritureEchec"))}
                      note="panne silencieuse : le service marche et repaie chaque fois"
                    />
                  </tbody>
                </table>
              </div>
              <p className="fine">
                Les mêmes taux, sans aucun volume, sont publiés sur <a href="/sante">/sante</a>,
                qui est ouvert. Une seule fonction les calcule : deux définitions du même taux qui
                divergeraient d'un point vaudraient moins que pas de taux du tout.
              </p>
            </div>

            <div data-groupe="mesure" className="block">
              <h2>Les volets facultatifs</h2>
              <p className="sub">
                Le volet 3 est passé de huit à seize questions pour rendre sa mesure moins facile à
                tromper. Ce gain a un prix en écrans, et il se lit ici.
              </p>
              <div className="tabwrap">
                <table className="tab compare">
                  <thead>
                    <tr>
                      <th>Volet</th>
                      <th>Ouverts</th>
                      <th>Terminés</th>
                      <th>Abandon</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td data-col="Volet">2 — coût du temps</td>
                      <td data-col="Ouverts">{fmt(n(c, "volet2Ouverts"))}</td>
                      <td data-col="Terminés">{fmt(n(c, "volet2Termines"))}</td>
                      <td data-col="Abandon">{pc(t.abandonVolet2)}</td>
                    </tr>
                    <tr>
                      <td data-col="Volet">3 — niveau de maîtrise</td>
                      <td data-col="Ouverts">{fmt(n(c, "volet3Ouverts"))}</td>
                      <td data-col="Terminés">{fmt(n(c, "volet3Termines"))}</td>
                      <td data-col="Abandon">{pc(t.abandonVolet3)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/*
              L'axe dirigeant, et les deux marches qui décident de son sort.

              Le pari du 18/08 est de demander le salaire moyen plutôt que le
              chiffre d'affaires, en supposant la question moins intrusive. Cette
              hypothèse n'a été éprouvée sur personne — et elle ne le sera pas
              par des entretiens, mais par ces deux chiffres.

              Ils restent séparés à dessein : un abandon massif au profil accuse
              la question d'argent, un abandon dans le questionnaire accuse sa
              longueur. Confondus, ils n'accusent plus rien.
            */}
            <div data-groupe="mesure" className="block">
              <h2>L'axe dirigeant</h2>
              <p className="sub">
                Deux marches, deux causes d'abandon distinctes. Le profil demande le secteur,
                l'effectif et le salaire moyen — c'est là que se juge la question d'argent. Le
                questionnaire pose ensuite sept postes — c'est là que se juge sa longueur.
              </p>
              <div className="tabwrap">
                <table className="tab compare">
                  <thead>
                    <tr>
                      <th>Marche</th>
                      <th>Atteinte</th>
                      <th>Franchie</th>
                      <th>Abandon</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td data-col="Marche">Profil — dont le salaire moyen</td>
                      <td data-col="Atteinte">{fmt(n(c, "dirigeantsDemarres"))}</td>
                      <td data-col="Franchie">{fmt(n(c, "dirigeantsQuestionnaires"))}</td>
                      <td data-col="Abandon">{pc(t.abandonProfilDirigeant)}</td>
                    </tr>
                    <tr>
                      <td data-col="Marche">Questionnaire — sept postes</td>
                      <td data-col="Atteinte">{fmt(n(c, "dirigeantsQuestionnaires"))}</td>
                      <td data-col="Franchie">{fmt(n(c, "dirigeantsResultats"))}</td>
                      <td data-col="Abandon">{pc(t.abandonQuestionnaireDirigeant)}</td>
                    </tr>
                    {/*
                      La marche que l'arbitrage du 18/08 rend décisive : « l'axe
                      dirigeant est d'abord un canal d'acquisition pour l'axe
                      salarié, et c'est à ce titre qu'il se juge ». Elle ne
                      porte pas de taux d'abandon — transmettre n'est pas la
                      suite obligée du parcours, c'est ce qu'on espère de lui.
                    */}
                    <tr>
                      <td data-col="Marche">Transmission à l'équipe</td>
                      <td data-col="Atteinte">{fmt(n(c, "dirigeantsResultats"))}</td>
                      <td data-col="Franchie">{fmt(n(c, "partagesEquipe"))}</td>
                      <td data-col="Abandon">—</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="fine">
                « — » signifie qu'aucun dirigeant n'est passé par cette marche. Un taux d'abandon à
                zéro sur un parcours que personne n'a emprunté se lirait comme une réussite.
              </p>
              <p className="fine">
                <b>La dernière ligne est celle qui juge l'axe.</b> Le rapport dirigeant paie
                l'acquisition ; ce sont les diagnostics que ses équipes font ensuite qui la
                rentabilisent. Un axe dirigeant qui vend et ne transmet pas est un axe qui coûte
                son CAC sans rien rapporter au reste — à lire avec « arrivées parrainées »
                ci-dessus, qui compte ce que ces partages provoquent réellement.
              </p>

              {/*
                Le sort des onze coefficients non sourcés se lit ici, pas dans
                une opinion. Tant que « restés sur le barème » est faible, les
                coefficients ne portent presque aucun montant réellement vu —
                leur sourçage peut attendre. S'il monte, il devient le chantier
                de crédibilité prioritaire, et ce tableau le dira avant
                quiconque.
              */}
              <div className="tabwrap" style={{ marginTop: "18px" }}>
                <table className="tab compare">
                  <thead>
                    <tr>
                      <th>Ce qui fonde le montant affiché</th>
                      <th>Résultats</th>
                      <th>Part</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td data-col="Fondement">Restés sur le barème sectoriel — non sourcé</td>
                      <td data-col="Résultats">
                        {fmt(Math.max(0, n(c, "dirigeantsEstimes") - n(c, "dirigeantsAffines")))}
                      </td>
                      <td data-col="Part">{pc(t.surBaremeDirigeant)}</td>
                    </tr>
                    <tr>
                      <td data-col="Fondement">Affinés avec un salaire réel après affichage</td>
                      <td data-col="Résultats">{fmt(n(c, "dirigeantsAffines"))}</td>
                      <td data-col="Part">{pc(t.affinageDirigeant)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            {/*
              Le rapport de progression — 28/09/2026.

              Toute l'offre se juge sur un seul rapport : la part des pages
              ouvertes qui finissent en rapport acheté. Les deux marches du
              milieu disent où l'on perd les gens — s'ils ne s'engagent pas, le
              plan n'a pas été lu ; s'ils mesurent sans acheter, c'est
              l'aperçu gratuit qui en dit assez, ou pas assez.
            */}
            <div data-groupe="mesure" className="block">
              <h2>Le rapport de progression</h2>
              <p className="sub">
                Éteint tant que le réglage « le rapport de progression à 90 jours » n'est pas
                allumé. Les pages s'ouvrent depuis le lien imprimé dans chaque rapport.
              </p>
              <div className="tabwrap">
                <table className="tab compare">
                  <thead>
                    <tr>
                      <th>Marche</th>
                      <th>Atteinte</th>
                      <th>Franchie</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td data-col="Marche">Engagement pris</td>
                      <td data-col="Atteinte">{fmt(n(c, "progressionsOuvertes"))}</td>
                      <td data-col="Franchie">{fmt(n(c, "engagementsPris"))}</td>
                    </tr>
                    <tr>
                      <td data-col="Marche">
                        Point d'étape à 30 jours — {fmt(n(c, "etapesCommencees"))} commencé,{" "}
                        {fmt(n(c, "etapesPasEncore"))} pas encore, {fmt(n(c, "etapesAbandonnees"))} abandonné
                      </td>
                      <td data-col="Atteinte">{fmt(n(c, "engagementsPris"))}</td>
                      <td data-col="Franchie">{fmt(n(c, "etapesRepondues"))}</td>
                    </tr>
                    <tr>
                      <td data-col="Marche">Mesure à 90 jours</td>
                      <td data-col="Atteinte">{fmt(n(c, "progressionsOuvertes"))}</td>
                      <td data-col="Franchie">{fmt(n(c, "progressionsMesurees"))}</td>
                    </tr>
                    <tr>
                      <td data-col="Marche">Rapport de progression acheté</td>
                      <td data-col="Atteinte">{fmt(n(c, "progressionsMesurees"))}</td>
                      <td data-col="Franchie">{fmt(n(c, "progressionsVendues"))}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              {/*
                La recommandation — 28/09/2026. Le score net de recommandation
                (promoteurs moins détracteurs, en points de pourcentage) n'est
                affiché qu'au-delà de trente notes : en dessous, il bouge de
                vingt points pour une seule réponse.
              */}
              <p className="sub" style={{ marginTop: "14px" }}>
                <b>Recommandation</b> — {fmt(n(c, "notes"))} note{n(c, "notes") >= 2 ? "s" : ""} de 0 à 10 :{" "}
                {fmt(n(c, "notesPromoteurs"))} de 9 ou 10, {fmt(n(c, "notesPassifs"))} de 7 ou 8,{" "}
                {fmt(n(c, "notesDetracteurs"))} de 0 à 6.{" "}
                {n(c, "notes") >= 30
                  ? `Score net : ${Math.round(((n(c, "notesPromoteurs") - n(c, "notesDetracteurs")) / n(c, "notes")) * 100)}.`
                  : "Score net affiché à partir de trente notes."}{" "}
                Messages de recommandation ouverts : {fmt(n(c, "recommandationsEnvoyees"))}.
              </p>
              <p className="fine">
                Les ouvertures comptent les visites, pas les personnes : une page revue trois fois
                compte trois fois. La dernière ligne, elle, compte des rapports livrés.
              </p>
            </div>
              <p className="fine">
                Les onze coefficients sectoriels ne sont dérivés d'aucune source publiée — c'est la
                limite avouée de l'axe. Ces deux chiffres mesurent combien de montants réellement
                vus reposent dessus : c'est eux qui décident si le sourçage est urgent ou peut
                attendre.
              </p>
            </div>

            <div data-groupe="mesure" className="block">
              <h2>Les quatorze derniers jours</h2>
              <div className="tabwrap">
                <table className="tab compare">
                  <thead>
                    <tr>
                      <th>Jour</th>
                      <th>Visites</th>
                      <th>Diagnostics</th>
                      <th>Indices</th>
                      <th>Partages</th>
                      <th>Ventes</th>
                      <th>Recette</th>
                      <th>Coût IA</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recents.map((j) => (
                      <tr key={j.jour}>
                        <td data-col="Jour">{j.jour}</td>
                        <td data-col="Visites">{fmt(j.vues)}</td>
                        <td data-col="Diagnostics">{fmt(j.diagnostics)}</td>
                        <td data-col="Indices">{fmt(j.scores)}</td>
                        <td data-col="Partages">{fmt(j.partages)}</td>
                        <td data-col="Ventes">{fmt(j.ventes)}</td>
                        <td data-col="Recette">{fmt(j.recetteXof)} F</td>
                        <td data-col="Coût IA">{fmt(j.depenseXof)} F</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="fine">
                Le trafic de recette — le contrôle que le produit fait de lui-même après chaque
                livraison — n'entre dans aucun de ces compteurs. Il ferait dire aux chiffres une
                vente qui n'en est pas une.
              </p>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

/**
 * Les quatre vues du tableau de bord — 25/08/2026.
 *
 * ## Ce qu'elles réparent
 *
 * Vingt-cinq sections sur une seule page. Elles y sont toutes légitimes — la
 * mesure et l'exploitation se répondent — mais on ne les consulte jamais
 * ensemble : ou bien quelque chose ne va pas et l'on cherche le geste à faire,
 * ou bien tout va bien et l'on regarde si les chiffres bougent. Ce sont deux
 * usages différents du même écran, et les empiler obligeait à parcourir l'un
 * pour atteindre l'autre.
 *
 * ## L'ordre des vues n'est pas alphabétique
 *
 * « Agir » vient en premier parce que c'est la seule vue dont le contenu coûte
 * de l'argent à ignorer : un acheteur qui attend son rapport, un paiement resté
 * ouvert, un service dû et non rendu. Les mesures peuvent attendre une heure ;
 * un acheteur débité, non.
 *
 * ## Pourquoi des boutons pressés et non un `tablist`
 *
 * Les sections gouvernées vivent dans **deux composants** et sont entremêlées
 * dans le flux de la page. Un vrai `role="tablist"` exige un panneau par
 * onglet, donc de réordonner tout l'écran pour regrouper physiquement ce qui
 * est aujourd'hui dispersé — un remaniement bien plus lourd que le confort
 * gagné, et qui casserait l'ordre voulu à l'intérieur de chaque vue.
 *
 * Ce sont donc des **filtres**, annoncés comme tels : `aria-pressed` dit lequel
 * est actif, et le lecteur d'écran entend « bouton, activé » plutôt qu'un
 * onglet qui promettrait un panneau qu'il ne trouverait pas. Nommer les choses
 * pour ce qu'elles sont vaut mieux que de porter le costume d'un motif qu'on
 * n'implémente qu'à moitié.
 */
type Vue = "agir" | "sante" | "mesure" | "reglages";

const VUES: ReadonlyArray<{ cle: Vue; libelle: string; quoi: string }> = [
  { cle: "agir", libelle: "À traiter", quoi: "Ce qui attend un geste : acheteurs, paiements, engagements." },
  { cle: "sante", libelle: "Le produit", quoi: "La rédaction, la dépense, la qualité, la sentinelle." },
  { cle: "mesure", libelle: "La mesure", quoi: "Les chiffres, l'entonnoir, l'économie, les deux axes." },
  { cle: "reglages", libelle: "Réglages et essais", quoi: "Les interrupteurs, les essais, le journal." },
];

function estVue(valeur: string): valeur is Vue {
  return VUES.some((v) => v.cle === valeur);
}

function vueDepuisFragment(): Vue {
  const brut = window.location.hash.replace(/^#/, "");
  return estVue(brut) ? brut : "agir";
}

function Barre({ vue, choisir }: { vue: Vue; choisir: (v: Vue) => void }) {
  const courante = VUES.find((v) => v.cle === vue);
  return (
    <div className="pil-vues">
      <div className="pil-vues-boutons" role="group" aria-label="Filtrer les sections">
        {VUES.map((v) => (
          <button
            key={v.cle}
            type="button"
            className="pil-vue"
            aria-pressed={v.cle === vue}
            onClick={() => choisir(v.cle)}
          >
            {v.libelle}
          </button>
        ))}
      </div>
      <p className="fine" aria-live="polite">
        {courante?.quoi}
      </p>
    </div>
  );
}
